import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Paper,
  Modal,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  Fade,
  Snackbar,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useFinalExam } from '../hooks/useFinalExam';
import { TAGS } from '../types/Tag';
import { tagToColor } from '../types/Tag';

interface CreateFinalExamModalProps {
  open: boolean;
  onClose: () => void;
}

const CreateFinalExamModal = ({ open, onClose }: CreateFinalExamModalProps) => {
  const [numberOfQuestions, setNumberOfQuestions] = useState<number>(50);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showError, setShowError] = useState(false);
  const [warningMessage, setWarningMessage] = useState<string>('');
  const [showWarning, setShowWarning] = useState(false);
  const [availableQuestions, setAvailableQuestions] = useState<number>(0);
  const [requestedQuestions, setRequestedQuestions] = useState<number>(0);

  const { handleCreateFinalExam } = useFinalExam();

  const handleTagToggle = (tag: string) => {
    setSelectedTags((prev) => {
      if (prev.includes(tag)) {
        return prev.filter((t) => t !== tag);
      } else {
        return [tag, ...prev];
      }
    });
  };

  const handleCreateExam = async () => {
    if (selectedTags.length === 0) {
      setErrorMessage(
        'Please select at least one organ system to create a final exam.',
      );
      setShowError(true);
      return;
    }

    setIsCreating(true);
    try {
      await handleCreateFinalExam(numberOfQuestions, selectedTags);
      onClose();
    } catch (error: any) {
      console.error('Error creating final exam:', error);

      // Extract meaningful error message from GraphQL error
      let errorMsg = 'Failed to create final exam. Please try again.';

      if (error?.message) {
        if (error.message.includes('No questions found')) {
          errorMsg = `No questions found for the selected organ systems: ${selectedTags.join(
            ', ',
          )}. Please try selecting different organ systems.`;
          setErrorMessage(errorMsg);
          setShowError(true);
        } else if (
          error.message.includes('Only') &&
          error.message.includes('questions available')
        ) {
          // Parse the numbers from the error message
          const match = error.message.match(
            /Only (\d+) questions available.*Requested: (\d+)/,
          );
          if (match) {
            const available = parseInt(match[1]);
            const requested = parseInt(match[2]);
            setAvailableQuestions(available);
            setRequestedQuestions(requested);
            setWarningMessage(
              `Only ${available} questions are available for the selected organ systems, but you requested ${requested}. Would you like to proceed with ${available} questions instead?`,
            );
            setShowWarning(true);
            return; // Don't show error, show warning instead
          } else {
            errorMsg = `Insufficient questions available for the selected organ systems. Please reduce the number of questions or select additional organ systems.`;
          }
        } else if (
          error.message.includes('already have an active final exam')
        ) {
          errorMsg =
            'You already have an active final exam. Please complete it before starting a new one.';
        } else {
          errorMsg = error.message;
        }
      }

      setErrorMessage(errorMsg);
      setShowError(true);
    } finally {
      setIsCreating(false);
    }
  };

  const handleClearFilters = () => {
    setSelectedTags([]);
  };

  const handleProceedWithFewerQuestions = async () => {
    setShowWarning(false);
    setIsCreating(true);
    try {
      await handleCreateFinalExam(availableQuestions, selectedTags);
      onClose();
    } catch (error: any) {
      console.error('Error creating final exam with fewer questions:', error);
      setErrorMessage('Failed to create final exam. Please try again.');
      setShowError(true);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby="create-final-exam-modal"
        aria-describedby="create-final-exam-modal-description"
      >
        <Fade in={open} timeout={500}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '85%',
              maxWidth: '600px',
              maxHeight: '85vh',
              overflow: 'auto',
              zIndex: 1000,
            }}
          >
            <Paper
              elevation={8}
              sx={{
                background:
                  'linear-gradient(135deg, rgb(255, 255, 255) 0%, #e6f3ff 100%)',
                border: '2px solid rgb(73, 132, 234)',
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(255, 179, 217, 0.3)',
                overflow: 'hidden',
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  background:
                    'linear-gradient(135deg, #ff4da6 0%, #4682b4 100%)',
                  p: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background:
                      'linear-gradient(45deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
                    pointerEvents: 'none',
                  },
                }}
              >
                <Typography
                  variant="h5"
                  component="h2"
                  sx={{
                    fontWeight: 700,
                    color: 'white',
                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  🎯 Take Final Exam ✨
                </Typography>
                <IconButton
                  onClick={onClose}
                  size="small"
                  sx={{
                    color: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.3)',
                      transform: 'scale(1.1)',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>

              {/* Content */}
              <Box sx={{ p: 3 }}>
                {/* Number of Questions */}
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                      mb: 1.5,
                      fontWeight: 600,
                      color: '#5a4a6a',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <span style={{ fontSize: '1.2rem' }}>📝</span>
                    Number of Questions
                  </Typography>
                  <ToggleButtonGroup
                    value={numberOfQuestions}
                    exclusive
                    onChange={(event, newValue) => {
                      if (newValue !== null) {
                        setNumberOfQuestions(newValue);
                      }
                    }}
                    aria-label="number of questions"
                    sx={{
                      '& .MuiToggleButton-root': {
                        px: 2.5,
                        py: 1,
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        border: '2px solid #e0e0e0',
                        borderRadius: 2,
                        transition: 'all 0.3s ease-in-out',
                        transform: 'translateY(0)',
                        '&.Mui-selected': {
                          background:
                            'linear-gradient(45deg, #ff4da6, #4682b4)',
                          color: 'white',
                          borderColor: 'transparent',
                          boxShadow: '0 6px 20px rgba(255, 102, 178, 0.4)',
                          '&:hover': {
                            background:
                              'linear-gradient(45deg, #ff66b2, #87ceeb)',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 8px 25px rgba(255, 102, 178, 0.5)',
                          },
                        },
                        '&:hover': {
                          backgroundColor: '#f5f5f5',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                        },
                      },
                    }}
                  >
                    <ToggleButton value={50} aria-label="50 questions">
                      50 Questions
                    </ToggleButton>
                    <ToggleButton value={100} aria-label="100 questions">
                      100 Questions
                    </ToggleButton>
                  </ToggleButtonGroup>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mt: 1.5,
                      display: 'block',
                      color: '#5f9ea0',
                      fontSize: '0.85rem',
                      fontStyle: 'italic',
                    }}
                  >
                    💡 Choose between 50 or 100 questions for your comprehensive
                    final exam
                  </Typography>
                </Box>

                {/* Organ Selection */}
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                      mb: 2,
                      fontWeight: 600,
                      color: '#5a4a6a',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <span style={{ fontSize: '1.2rem' }}>🫀</span>
                    Filter by Organ System
                  </Typography>

                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1,
                      mb: 2,
                      alignItems: 'center',
                    }}
                  >
                    {TAGS.map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        onClick={() => handleTagToggle(tag)}
                        size="small"
                        sx={{
                          backgroundColor: selectedTags.includes(tag)
                            ? tagToColor[tag] || '#BDBDBD'
                            : 'rgba(255, 255, 255, 0.8)',
                          color: selectedTags.includes(tag)
                            ? '#FFF'
                            : '#5a4a6a',
                          cursor: 'pointer',
                          fontSize: '0.8rem',
                          height: '28px',
                          fontWeight: 500,
                          border: selectedTags.includes(tag)
                            ? '2px solid rgba(255, 255, 255, 0.3)'
                            : '2px solid rgba(73, 132, 234, 0.3)',
                          transition: 'all 0.3s ease-in-out',
                          '& .MuiChip-label': {
                            px: 1.5,
                            py: 0.5,
                          },
                          '&:hover': {
                            backgroundColor: selectedTags.includes(tag)
                              ? tagToColor[tag] || '#BDBDBD'
                              : 'rgba(255, 255, 255, 0.95)',
                            transform: 'translateY(-2px) scale(1.05)',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                          },
                        }}
                      />
                    ))}
                  </Box>

                  {selectedTags.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleClearFilters}
                        sx={{
                          color: '#5f9ea0',
                          borderColor: '#5f9ea0',
                          fontSize: '0.75rem',
                          py: 0.5,
                          px: 1.5,
                          minHeight: '24px',
                          borderRadius: 2,
                          '&:hover': {
                            borderColor: '#4682b4',
                            color: '#4682b4',
                            backgroundColor: 'rgba(70, 130, 180, 0.05)',
                          },
                          transition: 'all 0.2s ease-in-out',
                        }}
                      >
                        🗑️ Clear Filters
                      </Button>
                    </Box>
                  )}

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      color: '#5f9ea0',
                      fontSize: '0.85rem',
                      fontStyle: 'italic',
                    }}
                  >
                    🎯 Select the organ systems you want to be tested on
                  </Typography>
                </Box>

                {/* Action Buttons */}
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    justifyContent: 'flex-end',
                    pt: 1.5,
                    borderTop: '1px solid rgba(73, 132, 234, 0.2)',
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={onClose}
                    size="medium"
                    sx={{
                      color: '#5f9ea0',
                      borderColor: '#5f9ea0',
                      px: 3,
                      py: 1,
                      borderRadius: 2,
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      '&:hover': {
                        borderColor: '#4682b4',
                        color: '#4682b4',
                        backgroundColor: 'rgba(70, 130, 180, 0.05)',
                        transform: 'translateY(-1px)',
                      },
                      transition: 'all 0.2s ease-in-out',
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleCreateExam}
                    disabled={isCreating || selectedTags.length === 0}
                    size="medium"
                    sx={{
                      background: 'linear-gradient(45deg, #ff4da6, #4682b4)',
                      color: 'white',
                      px: 3,
                      py: 1,
                      borderRadius: 2,
                      fontSize: '0.9rem',
                      fontWeight: 700,
                      textTransform: 'none',
                      letterSpacing: '0.5px',
                      boxShadow:
                        '0 6px 20px rgba(255, 102, 178, 0.4), 0 4px 15px rgba(70, 130, 180, 0.3)',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      backdropFilter: 'blur(10px)',
                      '&:hover': {
                        background: 'linear-gradient(45deg, #ff66b2, #87ceeb)',
                        transform: 'translateY(-3px) scale(1.02)',
                        boxShadow:
                          '0 12px 30px rgba(255, 102, 178, 0.6), 0 8px 25px rgba(70, 130, 180, 0.4)',
                        border: '2px solid rgba(255, 255, 255, 0.5)',
                      },
                      '&:active': {
                        transform: 'translateY(-1px) scale(1.01)',
                      },
                      '&:disabled': {
                        background: '#ccc',
                        transform: 'none',
                        boxShadow: 'none',
                        border: '2px solid #ccc',
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  >
                    {isCreating ? '🚀 Creating...' : '🎯 Start Final Exam'}
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Box>
        </Fade>
      </Modal>

      {/* Error Snackbar */}
      <Snackbar
        open={showError}
        autoHideDuration={8000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ zIndex: 2000 }}
      >
        <Alert
          onClose={() => setShowError(false)}
          severity="error"
          sx={{
            width: '100%',
            '& .MuiAlert-message': {
              fontSize: '0.9rem',
              lineHeight: 1.4,
            },
          }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>

      {/* Warning Dialog */}
      <Modal
        open={showWarning}
        onClose={() => setShowWarning(false)}
        aria-labelledby="warning-modal"
      >
        <Fade in={showWarning} timeout={300}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '90%',
              maxWidth: '500px',
              zIndex: 2000,
            }}
          >
            <Paper
              elevation={8}
              sx={{
                background:
                  'linear-gradient(135deg, rgb(255, 255, 255) 0%, #fff3cd 100%)',
                border: '2px solid #ffc107',
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(255, 193, 7, 0.3)',
                overflow: 'hidden',
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  background:
                    'linear-gradient(135deg, #ffc107 0%, #ff9800 100%)',
                  p: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant="h6"
                  component="h2"
                  sx={{
                    fontWeight: 700,
                    color: 'white',
                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  ⚠️ Question Count Warning
                </Typography>
                <IconButton
                  onClick={() => setShowWarning(false)}
                  size="small"
                  sx={{
                    color: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.3)',
                      transform: 'scale(1.1)',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>

              {/* Content */}
              <Box sx={{ p: 3 }}>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 3,
                    fontSize: '1rem',
                    lineHeight: 1.6,
                    color: '#5a4a6a',
                  }}
                >
                  {warningMessage}
                </Typography>

                {/* Action Buttons */}
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    justifyContent: 'flex-end',
                    pt: 1.5,
                    borderTop: '1px solid rgba(255, 193, 7, 0.2)',
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={() => setShowWarning(false)}
                    size="medium"
                    sx={{
                      color: '#ff9800',
                      borderColor: '#ff9800',
                      px: 3,
                      py: 1,
                      borderRadius: 2,
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      '&:hover': {
                        borderColor: '#f57c00',
                        color: '#f57c00',
                        backgroundColor: 'rgba(255, 152, 0, 0.05)',
                        transform: 'translateY(-1px)',
                      },
                      transition: 'all 0.2s ease-in-out',
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleProceedWithFewerQuestions}
                    disabled={isCreating}
                    size="medium"
                    sx={{
                      background: 'linear-gradient(45deg, #ff9800, #f57c00)',
                      color: 'white',
                      px: 3,
                      py: 1,
                      borderRadius: 2,
                      fontSize: '0.9rem',
                      fontWeight: 700,
                      textTransform: 'none',
                      letterSpacing: '0.5px',
                      boxShadow: '0 6px 20px rgba(255, 152, 0, 0.4)',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      '&:hover': {
                        background: 'linear-gradient(45deg, #f57c00, #ef6c00)',
                        transform: 'translateY(-3px) scale(1.02)',
                        boxShadow: '0 12px 30px rgba(255, 152, 0, 0.6)',
                        border: '2px solid rgba(255, 255, 255, 0.5)',
                      },
                      '&:active': {
                        transform: 'translateY(-1px) scale(1.01)',
                      },
                      '&:disabled': {
                        background: '#ccc',
                        transform: 'none',
                        boxShadow: 'none',
                        border: '2px solid #ccc',
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  >
                    {isCreating
                      ? '🚀 Creating...'
                      : `✅ Proceed with ${availableQuestions} Questions`}
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Box>
        </Fade>
      </Modal>
    </>
  );
};

export default CreateFinalExamModal;
